import { useState, useCallback } from 'react';
import { userService } from '../../../services/userService';
import { usePaginationState } from '../../../hooks/usePaginationState.js';
import { usePaginatedData } from '../../../hooks/usePaginatedData.js';

export const useUsers = (options = {}) => {
    const [actionLoading, setActionLoading] = useState(false);
    
    // Separate UI state from data fetching
    const paginationState = usePaginationState({
        defaultSort: 'username',
        defaultOrder: 'asc',
        defaultLimit: options.defaultLimit || 5,
        limitOptions: options.limitOptions || [5, 10, 25, 50]
    });

    const fetchUsers = useCallback(async (params) => {
        const result = await userService.getAllUsers(params);
        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.message);
        }
    }, []);

    const { data, pagination, loading, error, setError, refetch } = usePaginatedData(
        fetchUsers,
        paginationState
    );

    const createUser = useCallback(async (userData) => {
        try {
            setActionLoading(true);
            const result = await userService.createUser(userData);
            if (result.success) {
                refetch(); // Use refetch instead of manual reload
                return { success: true };
            } else {
                setError(result.message);
                // Don't refetch on error to avoid clearing the error message
                return { success: false, error: result.message };
            }
        } catch (err) {
            const errorMessage = err.message || 'Failed to create user';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    const updateUser = useCallback(async (id, userData) => {
        try {
            setActionLoading(true);
            const result = await userService.updateUser(id, userData);
            if (result.success) {
                refetch(); // Use refetch instead of manual reload
                return { success: true };
            } else {
                setError(result.message);
                // Don't refetch on error to avoid clearing the error message
                return { success: false, error: result.message };
            }
        } catch (err) {
            const errorMessage = err.message || 'Failed to update user';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    const deleteUser = useCallback(async (id, username) => {
        // Confirmation is now handled by the UI component
        try {
            setActionLoading(true);
            const result = await userService.deleteUser(id);
            if (result.success) {
                refetch(); // Use refetch instead of manual reload
                return { success: true };
            } else {
                setError(result.message);
                // Don't refetch on error to avoid clearing the error message
                return { success: false, error: result.message };
            }
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete user';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    // Return stable object with memoized properties
    return {
        // Data
        data,
        users: data, // Alias for backward compatibility
        pagination,
        loading,
        error,
        actionLoading,
        
        // Actions
        createUser,
        updateUser,
        deleteUser,
        setError,
        
        // Pagination controls
        ...paginationState
    };
};